import React, { useState } from 'react';
import { Wrench, Zap, Droplets, Bug, Scissors, Warehouse, Key, Shield, Brush, Waves, Grid, Home, AppWindow as Window } from 'lucide-react';
import type { ServiceType } from '../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '../../lib/supabase';
import { toast } from '@/components/ui/use-toast';

const SERVICES: { type: ServiceType; icon: React.ElementType; label: string; description: string }[] = [
  { type: 'HVAC', icon: Zap, label: 'HVAC', description: 'Heating, ventilation, and air conditioning services' },
  { type: 'Plumbing', icon: Droplets, label: 'Plumbing', description: 'Plumbing installation and repairs' },
  { type: 'Electrical', icon: Zap, label: 'Electrical', description: 'Electrical installation and repairs' },
  { type: 'Appliance', icon: Wrench, label: 'Appliance Repair', description: 'Repair and maintenance of home appliances' },
  { type: 'Pest', icon: Bug, label: 'Pest Control', description: 'Pest control and prevention services' },
  { type: 'Landscaping', icon: Scissors, label: 'Landscaping', description: 'Lawn care and landscaping services' },
  { type: 'Garage', icon: Warehouse, label: 'Garage', description: 'Garage door installation and repair' },
  { type: 'Locksmith', icon: Key, label: 'Locksmith', description: 'Lock installation and repair services' },
  { type: 'Security', icon: Shield, label: 'Security', description: 'Security system installation and monitoring' },
  { type: 'Cleaning', icon: Brush, label: 'Cleaning', description: 'Professional cleaning services' },
  { type: 'Pool', icon: Waves, label: 'Pool', description: 'Pool maintenance and repair' },
  { type: 'Flooring', icon: Grid, label: 'Flooring', description: 'Flooring installation and repair' },
  { type: 'Handyman', icon: Wrench, label: 'Handyman', description: 'General handyman services' },
  { type: 'Roofing', icon: Home, label: 'Roofing', description: 'Roof installation and repair' },
  { type: 'Windows', icon: Window, label: 'Windows', description: 'Window installation and repair' },
];

interface ServicesTabProps {
  selectedServices: ServiceType[];
  onServiceToggle: (services: ServiceType[]) => void;
}

const ServicesTab: React.FC<ServicesTabProps> = ({ selectedServices, onServiceToggle }) => {
  const [saving, setSaving] = useState(false);

  const handleServiceToggle = (service: ServiceType) => {
    const newServices = selectedServices.includes(service)
      ? selectedServices.filter(s => s !== service)
      : [...selectedServices, service];
    onServiceToggle(newServices);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('companies')
        .update({ 
          services: `{${selectedServices.join(',')}}` // Format as Postgres array literal
        })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Services updated successfully",
      });
    } catch (err) {
      console.error('Error saving services:', err);
      toast({
        title: "Error",
        description: "Failed to save services. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-lg font-medium">Services Offered</h2>
        <div className="flex items-center mt-1">
          <span className="text-muted-foreground">Select the services your company provides</span>
          {selectedServices.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {selectedServices.length} selected
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVICES.map(({ type, icon: Icon, label, description }) => (
          <Card
            key={type}
            onClick={() => handleServiceToggle(type)}
            className={cn(
              "cursor-pointer transition-all hover:shadow-md",
              selectedServices.includes(type) && "border-primary bg-primary/5"
            )}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start space-x-4">
                <div className={cn(
                  "p-2 rounded-lg transition-colors",
                  selectedServices.includes(type)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <CardTitle className={cn(
                    "text-lg",
                    selectedServices.includes(type) && "text-primary"
                  )}>
                    {label}
                  </CardTitle>
                  <CardDescription className={cn(
                    selectedServices.includes(type) && "text-primary/70"
                  )}>
                    {description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving || selectedServices.length === 0}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </>
  );
};

export default ServicesTab;